import { gql, useMutation } from "@apollo/client";
import {
  Alert,
  AlertIcon,
  Box,
  HStack,
  Icon,
  Input,
  Switch,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { HiRefresh } from "react-icons/hi";

import { Button, ConfirmModal } from "../../../components";
import { CopyIcon } from "../../../components/Icons/CopyIcon";
import { AiInterviewerFragment, AiInterviewerFragmentDoc } from "../../graphql";
import AiInterviewerSectionTitle from "./AiInterviewerSectionTitle";

const TOGGLE_AI_INTERVIEWER_STATIC_LINK = gql`
  mutation ToggleAiInterviewerStaticLink(
    $aiInterviewerId: ID!
    $enabled: Boolean!
    $regenerateSlug: Boolean
  ) {
    toggleAiInterviewerStaticLink(
      aiInterviewerId: $aiInterviewerId
      enabled: $enabled
      regenerateSlug: $regenerateSlug
    ) {
      aiInterviewer {
        ...AiInterviewer
      }
    }
  }
  ${AiInterviewerFragmentDoc}
`;

interface AiInterviewerStaticLinkSettingsProps {
  aiInterviewer: AiInterviewerFragment;
  readOnly: boolean;
}

const AiInterviewerStaticLinkSettings: React.FC<
  AiInterviewerStaticLinkSettingsProps
> = ({ aiInterviewer, readOnly }) => {
  const toast = useToast();
  const [isRegenerating, setIsRegenerating] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [toggleStaticLink, { loading }] = useMutation(
    TOGGLE_AI_INTERVIEWER_STATIC_LINK,
    {
      onCompleted: () => {
        // Only show this toast for toggle operations, not regenerate (which has its own toast)
        if (!isRegenerating) {
          toast({
            title: "Static link updated",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        }
      },
      onError: (error: Error) => {
        toast({
          title: "Error updating static link",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
    }
  );

  const handleToggle = (enable: boolean) => {
    toggleStaticLink({
      variables: {
        aiInterviewerId: aiInterviewer.id,
        enabled: enable,
        regenerateSlug: false,
      },
    });
  };

  const handleRegenerateConfirm = () => {
    setIsRegenerating(true);
    toggleStaticLink({
      variables: {
        aiInterviewerId: aiInterviewer.id,
        enabled: true,
        regenerateSlug: true,
      },
      onCompleted: () => {
        setIsRegenerating(false);
        toast({
          title: "Link regenerated",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      },
      onError: () => {
        setIsRegenerating(false);
      },
    });
  };

  const handleCopyLink = () => {
    if (aiInterviewer.staticLinkUrl) {
      navigator.clipboard.writeText(aiInterviewer.staticLinkUrl);
      toast({
        title: "Link copied to clipboard",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const isEnabled = aiInterviewer.staticLinkEnabled ?? false;
  const hasLink = !!aiInterviewer.staticLinkUrl;

  if (!aiInterviewer.position) {
    return (
      <Box>
        <AiInterviewerSectionTitle title="Manual invites" />
        <Text fontSize="sm" color="gray.600" mt={2}>
          Link this AI Interviewer to a position to enable invite links for
          ATS-less integration.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <AiInterviewerSectionTitle title="Manual invites" />
      <VStack spacing={4} align="stretch" mt={2}>
        <HStack justify="space-between">
          <Box>
            <Text fontSize="sm" fontWeight="500" mb={1}>
              Enable invite link
            </Text>
          </Box>
          <Switch
            isChecked={isEnabled}
            onChange={(e) => handleToggle(e.target.checked)}
            isDisabled={readOnly || loading}
            colorScheme="blue"
          />
        </HStack>
        <Text fontSize="sm" color="gray.600">
          These links are shareable and grant interview access to anyone who has
          the URL.
        </Text>
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            Hiring teams should distribute links only to the intended candidates
            to prevent unauthorized access, misuse, and unexpected
            interview-related costs.
          </Text>
        </Alert>

        {isEnabled && hasLink && (
          <Box>
            <Text fontSize="sm" fontWeight="500" mb={3}>
              Manage invite link:
            </Text>
            <Box>
              <Text fontSize="sm" color="gray.600" mb={1}>
                Online:
              </Text>
              <HStack>
                <Input
                  value={aiInterviewer.staticLinkUrl ?? ""}
                  readOnly
                  fontSize="sm"
                  bg="gray.50"
                />
                <Button
                  leftIcon={<CopyIcon />}
                  onClick={handleCopyLink}
                  size="sm"
                  variant="outline"
                  aria-label="Copy online link"
                />
                <Button
                  onClick={onOpen}
                  size="sm"
                  variant="outline"
                  aria-label="Regenerate online link"
                  isLoading={isRegenerating}
                  isDisabled={readOnly || loading}
                >
                  <Icon as={HiRefresh} />
                </Button>
              </HStack>
            </Box>
            <Text fontSize="xs" color="gray.600" mt={2}>
              Regenerating the link will cause any previous links to expire.
            </Text>
          </Box>
        )}
      </VStack>
      <ConfirmModal
        isOpen={isOpen}
        onCancel={onClose}
        onConfirm={() => {
          onClose();
          handleRegenerateConfirm();
        }}
        modalTitleText="Regenerate Link"
        modalBodyText="Are you sure you want to regenerate the link? The old link will no longer work."
        modalSubmitText="Regenerate"
        modalCancelText="Cancel"
      />
    </Box>
  );
};

export default AiInterviewerStaticLinkSettings;
